<template>
  <BaseDialog :title="topic?.title" :is-open="isOpen" type="basic" @close="emits('close')">
    <div v-if="topic" class="flex flex-col gap-4 pt-2">
      <div v-if="topic.icon || topic.excerpt" class="flex items-start gap-2">
        <BaseIcon v-if="topic.icon" class="text-on-surface-variant shrink-0">{{
          topic.icon
        }}</BaseIcon>
        <p v-if="topic.excerpt" class="text-body-medium text-on-surface-variant">
          {{ topic.excerpt }}
        </p>
      </div>
      <ul class="flex flex-col gap-3">
        <li v-for="questionGroup in topic.questionGroups" :key="questionGroup.id">
          <div
            class="mb-1.5 flex w-full flex-row items-center justify-between gap-2 overflow-hidden"
          >
            <h4
              v-if="questionGroup.title"
              class="text-title-small text-on-surface-variant shrink grow overflow-hidden text-ellipsis"
            >
              {{ questionGroup.title }}
            </h4>
            <div
              v-tooltip="scaleTooltip(questionGroup)"
              class="ml-auto flex shrink items-center justify-end overflow-hidden"
            >
              <span
                v-if="questionGroup.feedbackType === FeedbackType.ScaleBased"
                class="text-body-small flex shrink gap-1 overflow-hidden whitespace-nowrap"
              >
                <span class="overflow-hidden text-ellipsis">
                  {{ questionGroup.scale.items[0]!.label }}
                </span>
                <span> - </span>
                <span class="overflow-hidden text-ellipsis">
                  {{ questionGroup.scale.items[questionGroup.scale.items.length - 1]!.label }}
                </span>
              </span>
              <div
                class="text-on-primary-container bg-primary-container ml-1.5 h-6 shrink-0 rounded-full px-1"
              >
                <BaseIcon size="22">{{
                  questionGroup.feedbackType === FeedbackType.ScaleBased
                    ? 'linear_scale'
                    : 'short_text'
                }}</BaseIcon>
              </div>
            </div>
          </div>
          <ul class="flex flex-col gap-0.5">
            <li
              v-for="question in questionGroup.questions"
              :key="question.id"
              class="bg-surface rounded-extra-small first:rounded-t-large last:rounded-b-large flex flex-row p-4"
            >
              <p class="text-body-large text-on-surface-variant">
                {{ question.content }}
              </p>
            </li>
          </ul>
        </li>
      </ul>
    </div>
    <template #dismiss>
      <BaseTextButton color="text" @click="emits('close')">{{ $t('common.close') }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script lang="ts" setup>
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import { FeedbackType } from '@/core/models/feedback-type'
  import type { QuestionGroup } from '@/modules/give-feedback/models/question-group'
  import { useI18n } from 'vue-i18n'

  interface FeedbackTopicDetails {
    title: string
    icon?: string
    excerpt?: string
    questionGroups: ReadonlyArray<QuestionGroup>
  }

  interface Props {
    isOpen: boolean
    topic?: FeedbackTopicDetails | null
  }

  defineProps<Props>()

  const emits = defineEmits<{
    (e: 'close'): void
  }>()

  const { t } = useI18n()

  const scaleTooltip = (questionGroup: QuestionGroup) => {
    const typeLabel = t('feedbackType.' + questionGroup.feedbackType)
    if (questionGroup.feedbackType !== FeedbackType.ScaleBased) {
      return typeLabel
    }

    const first = questionGroup.scale.items[0]?.label
    const last = questionGroup.scale.items[questionGroup.scale.items.length - 1]?.label
    if (!first || !last) {
      return typeLabel
    }

    return `${typeLabel}: ${first} - ${last}`
  }
</script>
